import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import { Button, ErrorNote, Field, Input } from "../components/ui";
import { AuthFrame, CompactWordmark } from "../components/Brand";

/**
 * Asking for a reset link.
 *
 * The answer is the same whether the address has an account or not, which is
 * why the screen commits to it before knowing — anything that hesitated for one
 * address and not another would say out loud who banks here.
 */
export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setPending(true);

    try {
      await api<void>("/api/v1/auth/forgot-password", { method: "POST", body: { email } });
      setSent(true);
    } catch (caught) {
      // Only a rate limit or an outage reaches here. An unknown address is a
      // success as far as this screen is concerned.
      setError(caught instanceof ApiError ? caught.message : "Something went wrong. Try again.");
    } finally {
      setPending(false);
    }
  }

  if (sent) {
    return (
      <AuthFrame>
        <header className="space-y-2">
          <CompactWordmark />
          <h1 className="display pt-4 text-[2.5rem] text-ink lg:pt-0">Check your email</h1>
        </header>

        <p className="mt-6 max-w-prose text-sm leading-relaxed text-muted">
          If <span className="text-ink">{email}</span> has an account, a link to set a new password
          is on its way. It works once and expires in an hour.
        </p>
        <p className="mt-4 max-w-prose text-sm text-dim">
          Nothing arrived? Check the spam folder, then try again — the address may not be the one
          the account uses.
        </p>

        <div className="mt-8 flex flex-wrap gap-2">
          <Link to="/login">
            <Button variant="secondary">Back to sign in</Button>
          </Link>
          <Button variant="ghost" onClick={() => setSent(false)}>
            Use a different address
          </Button>
        </div>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame>
      <header className="space-y-2">
        <CompactWordmark />
        <h1 className="display pt-4 text-[2.5rem] text-ink lg:pt-0">Reset your password</h1>
        <p className="text-sm text-muted">
          We will email you a link to set a new one.
        </p>
      </header>

      <form onSubmit={submit} className="mt-8 space-y-4">
        <Field label="Email">
          <Input
            type="email"
            value={email}
            autoComplete="username"
            required
            autoFocus
            maxLength={254}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>

        {error !== null && <ErrorNote>{error}</ErrorNote>}

        <Button
          type="submit"
          variant="primary"
          className="min-h-[2.75rem] w-full"
          disabled={pending || email.length === 0}
        >
          {pending ? "Sending…" : "Send the link"}
        </Button>
      </form>

      <p className="mt-8 border-t border-hairline pt-6 text-sm text-dim">
        Remembered it?{" "}
        <Link to="/login" className="text-muted underline underline-offset-4 hover:text-ink">
          Sign in
        </Link>
        .
      </p>
    </AuthFrame>
  );
}
